import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Tag, Percent, DollarSign, Package, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Product categories for per-category discounts
const PRODUCT_CATEGORIES = [
  { id: 'roller-shades', name: 'Roller Shades' },
  { id: 'motorized-shades', name: 'Motorized Shades' },
  { id: 'blackout-shades', name: 'Blackout Shades' },
  { id: 'sheer-shades', name: 'Sheer Shades' },
  { id: 'outdoor-shades', name: 'Outdoor Shades' },
  { id: 'installation', name: 'Installation Services' },
  { id: 'accessories', name: 'Accessories' },
];

const emptyTier = {
  name: '',
  discount_percentage: 0,
  product_discounts: {},
  min_order_amount: '',
  description: ''
};

export const DiscountTiersTab = ({ token }) => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState(emptyTier);
  const [saving, setSaving] = useState(false);
  const [expandedTier, setExpandedTier] = useState(null);
  const [showCategoryDiscounts, setShowCategoryDiscounts] = useState(false);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/discount-tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(response.data);
    } catch (error) {
      console.error('Failed to fetch discount tiers:', error);
      toast.error('Failed to load discount tiers');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTier(null);
    setFormData(emptyTier);
    setShowCategoryDiscounts(false);
    setShowModal(true);
  };

  const openEditModal = (tier) => {
    setEditingTier(tier);
    setFormData({
      name: tier.name || '',
      discount_percentage: tier.discount_percentage || 0,
      product_discounts: tier.product_discounts || {},
      min_order_amount: tier.min_order_amount || '',
      description: tier.description || ''
    });
    setShowCategoryDiscounts(Object.keys(tier.product_discounts || {}).length > 0);
    setShowModal(true);
  };

  const saveTier = async () => {
    if (!formData.name) {
      toast.error('Tier name is required');
      return;
    }
    if (formData.discount_percentage < 0 || formData.discount_percentage > 100) {
      toast.error('Discount percentage must be between 0 and 100');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        product_discounts: showCategoryDiscounts ? formData.product_discounts : null,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        description: formData.description || null
      };

      if (editingTier) {
        await axios.put(`${API_URL}/admin/discount-tiers/${editingTier.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Discount tier updated');
      } else {
        await axios.post(`${API_URL}/admin/discount-tiers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Discount tier created');
      }
      fetchTiers();
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save discount tier');
    } finally {
      setSaving(false);
    }
  };

  const deleteTier = async (tierId) => {
    if (!window.confirm('Are you sure you want to delete this discount tier? Customers assigned to this tier will lose their discount.')) return;
    try {
      await axios.delete(`${API_URL}/admin/discount-tiers/${tierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discount tier deleted');
      fetchTiers();
    } catch (error) {
      toast.error('Failed to delete discount tier');
    }
  };

  const updateCategoryDiscount = (categoryId, value) => {
    const newDiscounts = { ...formData.product_discounts };
    if (value === '' || value === 0) {
      delete newDiscounts[categoryId];
    } else {
      newDiscounts[categoryId] = parseFloat(value) || 0;
    }
    setFormData({ ...formData, product_discounts: newDiscounts });
  };

  const toggleTierExpand = (tierId) => {
    setExpandedTier(expandedTier === tierId ? null : tierId);
  };

  return (
    <div data-testid="discount-tiers-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Discount Tiers</h2>
          <p className="text-neutral-500 text-sm">Manage pricing tiers for trade accounts and volume discounts</p>
        </div>
        <Button onClick={openCreateModal} className="btn-primary" data-testid="add-tier-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Discount Tier
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : tiers.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg">
          <Tag className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">No discount tiers created yet</p>
          <Button onClick={openCreateModal} className="mt-4" variant="outline">
            <Plus className="w-4 h-4 mr-2" /> Create Your First Tier
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tiers.map(tier => (
            <div 
              key={tier.id} 
              className="bg-white border border-neutral-200 rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openEditModal(tier)}
            >
              {/* Header with name and actions */}
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-serif text-xl">{tier.name}</h3>
                <div className="flex items-center gap-1">
                  <button 
                    onClick={(e) => { e.stopPropagation(); openEditModal(tier); }}
                    className="p-1 text-neutral-400 hover:text-neutral-600"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); deleteTier(tier.id); }}
                    className="p-1 text-neutral-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Large Percentage Display */}
              <div className="mb-2">
                <span className="text-4xl font-bold text-amber-500">{tier.discount_percentage}%</span>
              </div>
              
              {/* Description */}
              <p className="text-sm text-neutral-500 mb-3">off all products</p>
              
              {/* User count - placeholder for now */}
              <p className="text-xs text-neutral-400">
                {tier.customer_count || 0} customers
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              {editingTier ? `Edit: ${editingTier.name}` : 'Create Discount Tier'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Tier Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Gold, Platinum, VIP"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Base Discount (%)</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Minimum Order Amount</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <Input
                    type="number"
                    min="0"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                    placeholder="Optional"
                    className="pl-7"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Internal description"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Category Discounts Toggle */}
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowCategoryDiscounts(!showCategoryDiscounts)}
                className="w-full px-4 py-3 flex items-center justify-between bg-neutral-50 hover:bg-neutral-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-neutral-500" />
                  <span className="font-medium text-sm">Category-Specific Discounts</span>
                  <span className="text-xs text-neutral-400">(Override base discount for specific categories)</span>
                </div>
                {showCategoryDiscounts ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showCategoryDiscounts && (
                <div className="p-4 space-y-3">
                  <p className="text-xs text-neutral-500 mb-3">
                    Leave empty to use the base discount. Category discounts override the base discount.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {PRODUCT_CATEGORIES.map(category => (
                      <div key={category.id} className="flex items-center justify-between bg-neutral-50 rounded-lg px-3 py-2">
                        <span className="text-sm text-neutral-700">{category.name}</span>
                        <div className="relative w-20">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={formData.product_discounts[category.id] || ''}
                            onChange={(e) => updateCategoryDiscount(category.id, e.target.value)}
                            placeholder="-"
                            className="text-sm h-8 pr-6 text-right"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-800 mb-2">Preview</h4>
              <div className="text-sm text-amber-700">
                <p><strong>{formData.name || 'Tier Name'}</strong> customers will receive:</p>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>{formData.discount_percentage || 0}% off on all products (base discount)</li>
                  {showCategoryDiscounts && Object.entries(formData.product_discounts).map(([catId, discount]) => {
                    const category = PRODUCT_CATEGORIES.find(c => c.id === catId);
                    if (discount) {
                      return <li key={catId}>{discount}% off on {category?.name || catId}</li>;
                    }
                    return null;
                  })}
                  {formData.min_order_amount && <li>Minimum order: ${formData.min_order_amount}</li>}
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={saveTier} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : editingTier ? 'Update Tier' : 'Create Tier'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountTiersTab;
